import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, BookOpen, Barbell, Play, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function Programs() {
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    api.get("/library/programs").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get("/my/assignments").then(({ data }) => setAssignments(data)).catch(() => {});
    api.get("/my/sessions/upcoming?limit=5").then(({ data }) => setUpcoming(data)).catch(() => {});
  }, []);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="programs-page">
      <div className="mb-10">
        <div className="label-tiny text-brand-accent mb-2">// MY TRAINING</div>
        <h1 className="font-heading text-4xl sm:text-5xl mb-3">PROGRAMS</h1>
        <p className="text-text-secondary max-w-xl text-sm">Your coach-assigned plans, upcoming sessions, and the pre-built library.</p>
      </div>

      {/* Upcoming sessions */}
      {upcoming.length > 0 && (
        <section className="mb-10" data-testid="upcoming-sessions">
          <div className="label-tiny mb-3">// UPCOMING SESSIONS ({upcoming.length})</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {upcoming.map((s) => (
              <Link
                key={`${s.assignment_id}-${s.week}-${s.day}`}
                to={`/session/${s.assignment_id}/w/${s.week}/d/${s.day}`}
                className="border border-brand-accent/40 bg-brand-accent/[0.04] hover:bg-brand-accent/[0.08] transition p-4 group"
                data-testid={`upcoming-${s.assignment_id}-${s.week}-${s.day}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="label-tiny text-brand-accent text-[10px]">W{s.week} · D{s.day}{s.session_type ? ` · ${s.session_type.toUpperCase()}` : ""}</div>
                  <Play size={14} weight="fill" className="text-brand-accent group-hover:translate-x-0.5 transition" />
                </div>
                <div className="font-heading text-lg leading-tight">{s.session_name}</div>
                <div className="text-xs text-text-secondary mt-1">{s.program_name}</div>
                <div className="text-[10px] text-text-secondary mt-2 tabular-nums">
                  {s.exercise_count} exercise{s.exercise_count === 1 ? "" : "s"}{s.coach_name ? ` · Coach ${s.coach_name}` : ""}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Assigned coach programs */}
      {assignments.length > 0 && (
        <section className="mb-10" data-testid="assigned-programs">
          <div className="label-tiny mb-3">// ACTIVE ASSIGNMENTS ({assignments.length})</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {assignments.map((a) => (
              <AssignedProgramCard key={a.assignment_id} a={a} />
            ))}
          </div>
        </section>
      )}

      {/* Template library */}
      <section>
        <div className="label-tiny mb-3">// PROGRAM LIBRARY</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10" data-testid="programs-grid">
          {templates.map((p) => (
            <div key={p.id} className="bg-surface-DEFAULT hover:bg-surface-elevated transition-colors group cursor-pointer" data-testid={`program-card-${p.id}`}>
              <div className="aspect-video relative overflow-hidden bg-black">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute top-3 left-3 label-tiny bg-brand-accent text-surface-base px-2 py-1">
                  {p.category}
                </div>
                <div className="absolute bottom-3 right-3 label-tiny text-white/80">
                  {p.difficulty}
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-heading text-xl mb-2">{p.name}</h3>
                <p className="text-xs text-text-secondary mb-4 min-h-[2.5rem]">{p.description}</p>
                <div className="flex items-center justify-between border-t border-white/10 pt-3">
                  <div className="flex items-center gap-3 text-xs text-text-secondary">
                    <span className="flex items-center gap-1"><Clock size={12} /> <span className="tabular-nums">{p.weeks}W</span></span>
                    <span className="tabular-nums">{p.days_per_week}D/WK</span>
                  </div>
                  <ArrowRight size={16} className="text-brand-accent group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {templates.length === 0 && assignments.length === 0 && upcoming.length === 0 && (
          <div className="tactical-card p-12 text-center">
            <BookOpen size={36} className="text-brand-primary mx-auto mb-4" />
            <p className="text-text-secondary">Loading programs...</p>
          </div>
        )}
      </section>
    </div>
  );
}

function AssignedProgramCard({ a }) {
  const [completions, setCompletions] = useState([]);
  useEffect(() => {
    api.get(`/my/sessions/${a.assignment_id}/completions`).then(({ data }) => setCompletions(data)).catch(() => {});
  }, [a.assignment_id]);
  const prog = a.program || {};
  const days = (prog.days || []).filter((d) => !d.is_rest);
  const doneCount = completions.length;
  const pct = days.length ? Math.round((doneCount / days.length) * 100) : 0;
  const nextDay = days
    .slice()
    .sort((x, y) => (x.week - y.week) || (x.day - y.day))
    .find((d) => !completions.find((c) => c.week === d.week && c.day === d.day));

  return (
    <div className="border border-white/10 p-4" data-testid={`assignment-${a.assignment_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="font-heading text-lg">{a.program_name}</div>
          <div className="text-xs text-text-secondary mt-0.5">
            {prog.weeks || a.total_weeks || "?"} wk · {days.length} sessions · Coach {a.coach?.name || "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading text-2xl text-brand-accent tabular-nums" data-testid={`assignment-${a.assignment_id}-progress`}>{pct}%</div>
          <div className="text-[10px] text-text-secondary tabular-nums">{doneCount}/{days.length}</div>
        </div>
      </div>
      <div className="h-1.5 bg-white/5 mt-3">
        <div className="h-full bg-brand-accent transition-all" style={{ width: `${pct}%` }} />
      </div>
      {nextDay && (
        <Link
          to={`/session/${a.assignment_id}/w/${nextDay.week}/d/${nextDay.day}`}
          className="mt-3 inline-flex items-center gap-1.5 bg-brand-accent text-surface-base px-3 py-1.5 label-tiny tracking-wider text-[10px] hover:opacity-90"
          data-testid={`assignment-${a.assignment_id}-continue`}
        >
          <Play size={11} weight="fill" /> CONTINUE · W{nextDay.week}·D{nextDay.day}
        </Link>
      )}
      {!nextDay && doneCount > 0 && (
        <div className="mt-3 text-xs text-green-500 inline-flex items-center gap-1" data-testid={`assignment-${a.assignment_id}-done`}>
          <CheckCircle size={12} weight="fill" /> PROGRAM COMPLETE
        </div>
      )}
    </div>
  );
}
